import classNames from 'classnames';
import React, { useContext } from 'react';

import { SponsoredTag } from '@zooplus/ui-components';

import AppContext from '../../context';
import { Translations } from '../../types';
import Link from '../Link';
import { LinkTypeEnum } from '../Link/Link';
import { UiSuggestionSection } from '../SearchBar/engines/types';

import styles from './SearchSuggestion.module.scss';

const checkSuggestions = (
    suggestions: UiSuggestionSection[],
    term: string,
    translations: Translations,
    searchFlyoutSponsoredTagEnabled: boolean,
) => {
    const categorySection = suggestions.find((s) => s.type === 'CATEGORY');
    const productSection = suggestions.find((s) => s.type === 'PRODUCT');
    const serviceSection = suggestions.find((s) => s.type === 'SERVICE');

    let layout: React.JSX.Element | null = null;

    if (categorySection || productSection || serviceSection) {
        layout = (
            <>
                {categorySection && (
                    <>
                        <span className={styles.searchHeader}>
                            {`${translations.get(
                                'shop.header.search.autocomplete.title.suggestion',
                            )}`}{' '}
                            <span className={styles.searchTerm}>{term}</span>
                        </span>
                        {categorySection.items.map((category) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${category.title}`}>
                                <Link
                                    bold={false}
                                    dataZta="search-flyout-suggestion-category-link"
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    href={category.url}
                                    className={classNames(
                                        styles.suggestionLink,
                                        styles.suggestionLinkCategory,
                                    )}>
                                    <span
                                        className={styles.truncatedText}
                                        dangerouslySetInnerHTML={{
                                            __html: `${category.title}`,
                                        }}
                                    />
                                    {category.count && (
                                        <span>{`(${category.count})`}</span>
                                    )}
                                </Link>
                            </div>
                        ))}
                    </>
                )}

                {productSection && (
                    <>
                        <span
                            className={classNames(
                                styles.searchHeader,
                                styles.searchHeaderMargin,
                            )}>
                            {translations.get(
                                'shop.header.search.autocomplete.title.products',
                            )}
                        </span>
                        {productSection.items.map((product) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${product.title}`}>
                                <Link
                                    bold={false}
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    dataZta="search-flyout-suggestion-product-link"
                                    href={product.url}
                                    className={styles.suggestionLink}>
                                    <span
                                        className={styles.productTitleWrapper}>
                                        <img
                                            className={styles.searchImage}
                                            src={product.picture}
                                            alt=""
                                        />
                                        <span
                                            className={styles.truncatedText}
                                            dangerouslySetInnerHTML={{
                                                __html: product.title,
                                            }}
                                        />
                                        {searchFlyoutSponsoredTagEnabled &&
                                            product.isSponsored && (
                                                <SponsoredTag
                                                    className={
                                                        styles.sponsoredTag
                                                    }>
                                                    {translations.get(
                                                        'general.sponsored.products',
                                                    )}
                                                </SponsoredTag>
                                            )}
                                    </span>
                                </Link>
                            </div>
                        ))}
                    </>
                )}

                {serviceSection && (
                    <>
                        <span
                            className={classNames(
                                styles.searchHeader,
                                styles.searchHeaderMargin,
                            )}>
                            {translations.get(
                                'shop.header.search.autocomplete.title.services',
                            )}
                        </span>
                        {serviceSection.items.map((service) => (
                            <div
                                className={styles.suggestionItem}
                                key={`item-${service.title}`}>
                                <Link
                                    bold={false}
                                    linkType={LinkTypeEnum.NEUTRAL}
                                    href={service.url}
                                    dataZta="search-flyout-suggestion-service-link"
                                    className={styles.suggestionLink}>
                                    <span
                                        className={styles.truncatedText}
                                        dangerouslySetInnerHTML={{
                                            __html: service.title,
                                        }}
                                    />
                                </Link>
                            </div>
                        ))}
                    </>
                )}
            </>
        );
    }

    return layout;
};

export interface SearchSuggestionProps {
    suggestions: UiSuggestionSection[];
    term: string;
    id: string;
    device: 'mobile' | 'desktop';
}

const SearchSuggestion = ({
    suggestions,
    term,
    device,
}: SearchSuggestionProps): React.JSX.Element => {
    const { translations, searchFlyoutSponsoredTagEnabled } =
        useContext(AppContext);
    return (
        <div
            className={styles.suggestionList}
            data-zta={`search_suggestions_${device}`}>
            {checkSuggestions(
                suggestions,
                term,
                translations,
                searchFlyoutSponsoredTagEnabled,
            )}
        </div>
    );
};

export default SearchSuggestion;
